import { useRef, useState } from 'react';
import { useApp } from '../lib/app';
import { supabase } from '../lib/supabase';
import { useRows } from '../lib/useRows';
import { downloadFile, write } from '../lib/api';
import { uploadFile } from '../lib/files';
import { fmtBytes, fmtDateTime } from '../lib/format';
import { Badge, Button, Card, Empty, PageHeader, errText, useToast } from '../ui/kit';
import { canView, useViewer } from '../ui/Viewer';

type F = {
  id: string; bucket: string; original_name: string; title: string | null; mime: string | null; size_bytes: number | null;
  width: number | null; height: number | null; original_kept: boolean; created_at: string; deleted_at: string | null;
};
const NONE = '00000000-0000-0000-0000-000000000000';

export default function Files() {
  const { siteId, site, can } = useApp();
  const toast = useToast();
  const viewer = useViewer();
  const input = useRef<HTMLInputElement>(null);
  const [keep, setKeep] = useState(false);
  const [bucket, setBucket] = useState<'auto' | 'documents' | 'photos' | 'drawings'>('auto');
  const [busy, setBusy] = useState<string | null>(null);
  const [over, setOver] = useState(false);

  const list = useRows<F>(
    () => supabase.from('files').select('id, bucket, original_name, title, mime, size_bytes, width, height, original_kept, created_at, deleted_at')
      .eq('site_id', siteId ?? NONE).is('deleted_at', null).order('created_at', { ascending: false }).limit(200),
    [siteId],
    ['files'],
  );
  const editable = can('files', 4);

  async function upload(files: FileList | File[]) {
    if (!siteId) return;
    for (const f of Array.from(files)) {
      setBusy(`Uploading ${f.name}…`);
      try {
        await uploadFile(f, { siteId, keepOriginal: keep, bucket: bucket === 'auto' ? undefined : bucket });
        toast({ tone: 'ok', text: `Uploaded ${f.name}` });
      } catch (e) {
        toast({ tone: 'error', text: `${f.name}: ${errText(e)}` });
      }
    }
    setBusy(null);
    void list.reload();
  }

  async function remove(f: F) {
    if (!window.confirm(`Delete ${f.original_name}? It can be restored from the audit log.`)) return;
    try {
      await write('files', 'delete', { id: f.id, summary: `Deleted file ${f.original_name}` });
      void list.reload();
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    }
  }

  return (
    <>
      <PageHeader title="Files" sub={`${site?.name ?? ''} · test page for uploads and downloads (registers attach files from V0.2)`} />
      {editable ? (
        <div
          className="card"
          style={{ border: `2px dashed ${over ? '#c55a11' : '#d9cbb9'}`, padding: 18, display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'center' }}
          onDragOver={(e) => { e.preventDefault(); setOver(true); }}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => { e.preventDefault(); setOver(false); void upload(e.dataTransfer.files); }}
        >
          <div style={{ flex: '1 1 260px' }}>
            <strong>Drop files here or choose them</strong>
            <div className="muted small">Photos are resized to 2,048 px on this device before upload, unless "keep original" is ticked.</div>
          </div>
          <label className="check small"><input type="checkbox" checked={keep} onChange={(e) => setKeep(e.target.checked)} /> Keep original</label>
          <select className="lv-select" style={{ minHeight: 38 }} value={bucket} onChange={(e) => setBucket(e.target.value as typeof bucket)} aria-label="Store as">
            <option value="auto">Store as: automatic</option>
            <option value="documents">Document</option>
            <option value="photos">Photo</option>
            <option value="drawings">Drawing</option>
          </select>
          <input ref={input} type="file" multiple hidden onChange={(e) => e.target.files && void upload(e.target.files)} />
          <Button kind="primary" disabled={!!busy || !siteId} onClick={() => input.current?.click()}>{busy ?? 'Choose files'}</Button>
        </div>
      ) : null}
      <Card pad={false}>
        {list.loading ? <Empty>Loading…</Empty> : list.rows.length === 0 ? <Empty>No files on this site yet.</Empty> : (
          <div className="table-wrap">
            <table className="t">
              <thead><tr><th>File</th><th>Type</th><th>Size</th><th>Uploaded</th><th></th></tr></thead>
              <tbody>
                {list.rows.map((f) => (
                  <tr key={f.id}>
                    <td>{canView({ id: f.id, name: f.original_name, mime: f.mime }) ? <button type="button" className="linkbtn" onClick={() => viewer({ id: f.id, name: f.original_name, mime: f.mime }, list.rows.map((x) => ({ id: x.id, name: x.original_name, mime: x.mime })))}><strong>{f.original_name}</strong></button> : <strong>{f.original_name}</strong>}{f.width ? <div className="muted small">{f.width}×{f.height}{f.original_kept ? ' · original' : ''}</div> : null}</td>
                    <td><Badge>{f.bucket}</Badge></td>
                    <td>{fmtBytes(f.size_bytes)}</td>
                    <td>{fmtDateTime(f.created_at)}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <Button kind="ghost" onClick={() => downloadFile(f.id).catch((e) => toast({ tone: 'error', text: errText(e) }))}>Download</Button>
                      {editable ? <Button kind="ghost" onClick={() => void remove(f)}>Delete</Button> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
